import { useCallback, useEffect, useMemo, useState } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import enviro from 'enviro';
import PortalIdParser from 'PortalIdParser';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Form } from 'foundations-components/transitional/components/Form';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Section } from 'foundations-components/transitional/components/Section';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import { NewPropertiesForm } from '../form/NewPropertiesForm';
import ConditionalPropertiesPanelProgress from './ConditionalPropertiesPanelProgress';
import ConditionalPropertiesPanelSkeletonLoader from './ConditionalPropertiesPanelSkeletonLoader';
import { logPageView, trackConditionalPropertiesPanelSubmit } from '../utils/usageTrackingUtils';
import ConditionalPropertiesPanelDependentSection from './ConditionalPropertiesPanelDependentSection';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { buildPropertiesFormInitializingState } from '../form/validation/utils/status';
import { PropertiesProvider } from '../context/PropertiesProvider';
import { useGetIsUngatedForConditionalAssociations } from '../v2/hooks/useGetIsUngatedForConditionalAssociations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BOUNDARY_NAME = 'CustomerDataProperties_ConditionalPropertiesPanelError';
export const REFRESH_LIMIT = 3;
const isRevenuePortal = () => {
  const BETPortalId = enviro.isQa() ? 99535353 : 53;
  return BETPortalId === PortalIdParser.get();
};
const isRevenuePortalMemoized = memoizeOne(isRevenuePortal);
const getErrorComponent = ({
  onClose
}) => {
  return /*#__PURE__*/_jsxs(ErrorMessage, {
    title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "conditionalPropertiesPanel.errorTitle"
    }),
    children: [/*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "conditionalPropertiesPanel.errorDescriptionErrorBoundary"
      })
    }), /*#__PURE__*/_jsx(Button, {
      use: "tertiary-light",
      onClick: onClose,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "conditionalPropertiesPanel.closeButton"
      })
    })]
  });
};
const ConditionalPropertiesPanel = ({
  enableDependentAssociations = false,
  objectId,
  objectTypeId,
  triggerPropertyDefinition,
  properties,
  onApply,
  onCancel,
  defaultValues,
  getAdditionalPropertyValue,
  showAutoSaveText = false,
  renderInput
}) => {
  const isUngatedForConditionalAssociations = useGetIsUngatedForConditionalAssociations({
    enableDependentAssociations
  });
  useComponentMountMetric('ConditionalPropertiesPanel');
  const [draftValues, setDraftValues] = useState(defaultValues || {});
  const [formStatus, setFormStatus] = useState(buildPropertiesFormInitializingState());
  const [formKey, setFormKey] = useState(0);
  useEffect(() => {
    logPageView({
      subscreen: 'conditional-properties-panel'
    });
  }, []);
  const setFormValue = useCallback((propertyName, value) => {
    setDraftValues(prev => Object.assign({}, prev, {
      [propertyName]: value
    }));
  }, []);
  const handleFormStatusChange = useCallback(updatedStatus => {
    setFormStatus(updatedStatus);
  }, [setFormStatus]);
  const handleChange = useCallback(changes => {
    setDraftValues(changes);
  }, [setDraftValues]);
  const handleRefreshClick = useCallback(() => {
    if (formKey >= REFRESH_LIMIT) {
      return;
    }
    setFormStatus(buildPropertiesFormInitializingState());
    setFormKey(prev => prev + 1);
  }, [formKey]);
  const renderInputWithHelpers = useMemo(() => {
    if (!renderInput) {
      return undefined;
    }
    return input => {
      const helpers = {
        setValue: value => setFormValue(input.property.name, value)
      };
      return renderInput(input, helpers);
    };
  }, [renderInput, setFormValue]);
  const renderDependentSection = useCallback((input, passthroughProps) => {
    return /*#__PURE__*/_jsx(ConditionalPropertiesPanelDependentSection, {
      input: input,
      passthroughProps: passthroughProps,
      renderInput: renderInputWithHelpers,
      enableDependentAssociations: enableDependentAssociations
    });
  }, [renderInputWithHelpers, enableDependentAssociations]);
  const isSaveable = formStatus.saveable;
  const handleSubmit = useCallback(() => {
    trackConditionalPropertiesPanelSubmit();
    onApply(draftValues);
  }, [draftValues, onApply]);
  return /*#__PURE__*/_jsx(PropertiesProvider, {
    children: /*#__PURE__*/_jsx(ModalPanel, {
      width: '520px',
      "data-test-id": "conditional-properties-panel",
      children: /*#__PURE__*/_jsx(ErrorBoundary, {
        ErrorComponent: getErrorComponent,
        errorProps: {
          onClose: onCancel
        },
        boundaryName: BOUNDARY_NAME,
        showRefreshAlert: false,
        children: /*#__PURE__*/_jsxs(Form, {
          onSubmit: evt => {
            evt.preventDefault(); // don't submit the whole page
            evt.stopPropagation(); // don't submit any parent forms either
            if (isSaveable) {
              handleSubmit();
            }
          },
          children: [/*#__PURE__*/_jsxs(DialogHeader, {
            children: [/*#__PURE__*/_jsx(H2, {
              children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: isUngatedForConditionalAssociations ? 'conditionalPropertiesPanel.dependentLogicTitle' : 'conditionalPropertiesPanel.title'
              })
            }), /*#__PURE__*/_jsx(DialogCloseButton, {
              onClick: () => {
                onCancel();
              }
            })]
          }), formStatus.initializing && /*#__PURE__*/_jsx(ConditionalPropertiesPanelProgress, {}), /*#__PURE__*/_jsxs(DialogBody, {
            children: [/*#__PURE__*/_jsx(Section, {
              children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.description",
                options: {
                  triggerPropertyLabel: triggerPropertyDefinition.label
                }
              })
            }), /*#__PURE__*/_jsxs(Section, {
              children: [formStatus.initializing && /*#__PURE__*/_jsx(ConditionalPropertiesPanelSkeletonLoader, {}), formStatus.initializationFailed && /*#__PURE__*/_jsxs(ErrorMessage, {
                "data-test-id": "conditional-properties-panel-error",
                title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                  message: "conditionalPropertiesPanel.errorTitle"
                }),
                children: [/*#__PURE__*/_jsx("p", {
                  children: formKey < REFRESH_LIMIT ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: 'conditionalPropertiesPanel.errorDescriptionWithRefresh'
                  }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: 'conditionalPropertiesPanel.errorDescriptionNoRefresh'
                  })
                }), formKey < REFRESH_LIMIT && /*#__PURE__*/_jsx("p", {
                  children: /*#__PURE__*/_jsx(Button, {
                    use: "transparent",
                    onClick: handleRefreshClick,
                    children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                      message: "conditionalPropertiesPanel.errorRefreshButton"
                    })
                  })
                })]
              }), !formStatus.initializationFailed && /*#__PURE__*/_jsx(NewPropertiesForm, {
                objectId: objectId,
                objectTypeId: objectTypeId,
                getSavedValue: getAdditionalPropertyValue,
                enableDependentAssociations: enableDependentAssociations,
                values: draftValues,
                onChange: handleChange,
                onStatusChange: handleFormStatusChange,
                properties: properties,
                alwaysShowConditionalProperties: true,
                alwaysShowConditionalPropertyOptionsWithErrors: true,
                renderDependentSection: renderDependentSection,
                showPropertyDescription: isRevenuePortalMemoized(),
                renderInput: renderInputWithHelpers
              }, formKey)]
            })]
          }), /*#__PURE__*/_jsxs(DialogFooter, {
            children: [/*#__PURE__*/_jsx(Button, {
              use: "primary",
              type: "submit",
              disabled: !isSaveable,
              children: showAutoSaveText ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.saveButton"
              }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.applyButton"
              })
            }), /*#__PURE__*/_jsx(Button, {
              use: "tertiary-light",
              onClick: () => {
                onCancel();
              },
              children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "conditionalPropertiesPanel.cancelButton"
              })
            })]
          })]
        })
      })
    })
  });
};
export default ConditionalPropertiesPanel;